import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import './Dashboard.css';
import { useTemporada } from '../context/TemporadaContext';
import { useAuth } from '../context/AuthContext';
import { usePantallas } from '../context/PantallasContext';
import GuardaPantalla from './GuardaPantalla';
import { RUTA_ALTA_USUARIO, RUTA_PANEL_PANTALLAS } from '../utils/pantallas';
import { getTemporadasReal } from '../services/temporadasService';
import { getVersionFrontend, getVersionBackend } from '../services/versionService';

// Compacto para el badge: '07/09 20:15'. `builtAt` es ISO; si falta o no es
// parseable, se omite en vez de mostrar "Invalid Date".
const formatFecha = (builtAt) => {
  if (!builtAt) return null;
  const fecha = new Date(builtAt);
  if (Number.isNaN(fecha.getTime())) return null;
  return fecha.toLocaleString('es-ES', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
};

const NAV_ITEMS = [
  { to: '/privado',                label: 'Inicio' },
  { to: '/privado/ranking',        label: 'Ranking' },
  { to: '/privado/equipos',        label: 'Equipos' },
  { to: '/privado/jugadores',      label: 'Jugadores' },
  { to: '/privado/jornadas',       label: 'Jornadas' },
  { to: '/privado/calendario',     label: 'Calendario' },
  { to: '/privado/actas',          label: 'Actas' },
  { to: '/privado/form',           label: 'Introducir resultado' },
  { to: '/privado/competicion-2',  label: 'Competición' },
  { to: '/privado/temporadas',     label: 'Temporadas' },
  { to: '/privado/aplazamiento',    label: 'Aplazamiento' },
  { to: '/privado/revisar-aplazamiento', label: 'Revisar aplazamientos' },
];

const CLAVE_SIDEBAR = 'dashboard.sidebarPlegada';

// Recuerda si el menú lateral estaba plegado. localStorage puede fallar
// (modo privado, datos bloqueados): en ese caso se parte siempre desplegado.
const leerPlegada = () => {
  try { return localStorage.getItem(CLAVE_SIDEBAR) === '1'; } catch { return false; }
};

function AppLayout() {
  const { temporada, setTemporada } = useTemporada();
  const [temporadas, setTemporadas] = useState([]);
  const [versionFrontend, setVersionFrontend] = useState(null);
  const [versionBackend, setVersionBackend] = useState(null);
  const [plegada, setPlegada] = useState(leerPlegada);
  const location = useLocation();
  const { usuario } = useAuth();
  const { esVisible } = usePantallas();
  // "Mi área personal" solo tiene sentido si el usuario está vinculado a un jugador.
  const navItems = (usuario?.jugadorId ? [{ to: '/mi-area-personal', label: 'Mi área personal' }, ...NAV_ITEMS] : NAV_ITEMS)
    .filter(({ to }) => esVisible(to));
  // El panel de visibilidad no depende de la tabla: solo lo ve ADMIN.
  if (usuario?.rol === 'ADMIN') {
    navItems.push({ to: RUTA_PANEL_PANTALLAS, label: 'Pantallas' });
    navItems.push({ to: RUTA_ALTA_USUARIO, label: 'Alta de usuarios' });
  }

  const alternarSidebar = () => {
    setPlegada((p) => {
      try { localStorage.setItem(CLAVE_SIDEBAR, p ? '0' : '1'); } catch { /* sin persistencia */ }
      return !p;
    });
  };

  useEffect(() => {
    getVersionFrontend().then(setVersionFrontend);
    getVersionBackend().then(setVersionBackend);
  }, []);

  useEffect(() => {
    getTemporadasReal().then(ts => {
      setTemporadas(ts);
      if (!temporada || !ts.find(t => t.id === temporada)) {
        const activa = ts.find(t => t.estado === 'INICIADA') ?? ts[ts.length - 1];
        if (activa) setTemporada(activa.id);
      }
    }).catch(() => {});
  }, [usuario?.rol]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={`dashboard-layout${plegada ? ' sidebar-plegada' : ''}`}>
      <aside className="sidebar" id="menu-lateral" aria-hidden={plegada}>
        <div className="sidebar-brand">
          <span className="sidebar-brand-icon">LB</span>
          <span className="sidebar-brand-text">Liga Bádminton</span>
          <button
            type="button"
            className="sidebar-toggle sidebar-toggle-plegar"
            onClick={alternarSidebar}
            aria-label="Plegar menú lateral"
            aria-controls="menu-lateral"
            aria-expanded={!plegada}
            title="Plegar menú"
            tabIndex={plegada ? -1 : 0}
          >‹</button>
        </div>

        <div className="sidebar-temporada">
          <label className="sidebar-temporada-label" htmlFor="temporada-sel">Temporada</label>
          <select
            id="temporada-sel"
            value={temporada ?? ''}
            onChange={e => setTemporada(e.target.value)}
          >
            {temporadas.map(t => (
              <option key={t.id} value={t.id}>{t.nombre}</option>
            ))}
          </select>
        </div>

        <nav className="sidebar-nav">
          {navItems.map(({ to, label }) => (
            <Link
              key={to}
              to={to}
              className={`sidebar-nav-item${location.pathname === to ? ' active' : ''}`}
            >
              {label}
            </Link>
          ))}
        </nav>

        <div className="sidebar-footer">
          <Link to="/jornadas" className="sidebar-logout">Ir al área pública</Link>
          <Link to="/" className="sidebar-logout">Cerrar sesión</Link>
        </div>
      </aside>

      <button
        type="button"
        className="sidebar-toggle sidebar-toggle-desplegar"
        onClick={alternarSidebar}
        aria-label="Desplegar menú lateral"
        aria-controls="menu-lateral"
        aria-expanded={!plegada}
        title="Desplegar menú"
      >›</button>

      <main className="dashboard-main">
        <GuardaPantalla inicio="/privado" />
      </main>

      {usuario?.rol === 'ADMIN' && (
        <div className="version-badge">
          <span>
            Front {versionFrontend ? `v${versionFrontend.version} (${versionFrontend.commit})` : '—'}
            {versionFrontend && formatFecha(versionFrontend.builtAt) && ` · ${formatFecha(versionFrontend.builtAt)}`}
          </span>
          <span>
            Back {versionBackend ? `v${versionBackend.version} (${versionBackend.commit})` : '—'}
            {versionBackend && formatFecha(versionBackend.builtAt) && ` · ${formatFecha(versionBackend.builtAt)}`}
          </span>
        </div>
      )}
    </div>
  );
}

export default AppLayout;
